import { parseSnbt } from "./snbtReader";
import { cbItemByWireId } from "./cbItems";
import { customPotionEffectKey, decodePotionDamage, potionEffectKey } from "./potionBitfield";
import { parseItemKey, type CraftingResolverService } from "./craftingResolver";
import type { ItemStack, ItemStackNbt } from "../models/itemStack";

const POTION_IDS = new Set([373, 438, 441]);

export function stripColorCodes(s: string): string {
    return s.replace(/§[0-9a-fk-or]/gi, "");
}

// The key-shaped entry point into the one name resolution below, for the many call sites that hold an
// "id:meta" (or a custom item's "id:meta:nbthash") rather than a stack. Pass the snbt where one exists:
// a key can only ever name the vanilla material, never what the item calls itself in game.
export function resolveItemKeyDisplayName(
    key: string,
    snbt: string | null | undefined,
    resolver: CraftingResolverService | null | undefined,
): string {
    const parsed = parseItemKey(key);
    if (!parsed) return resolver?.getLocalizedName(key) ?? key;
    return resolveItemDisplayName({ itemId: parsed.id, damage: parsed.metadata, snbt: snbt ?? undefined }, resolver);
}

export function resolveItemDisplayName(
    stack: Pick<ItemStack, "itemId" | "damage" | "snbt" | "name">,
    resolver: CraftingResolverService | null | undefined,
): string {
    const n = parseSnbt(stack.snbt);
    if (n.displayName) return stripColorCodes(n.displayName);

    const cb = cbItemByWireId(stack.itemId);
    if (cb) return cb.displayName;

    const r = resolver;
    let def = stack.itemId >= 0
        ? (r?.getItemById(stack.itemId, stack.damage) ?? r?.getItemById(stack.itemId, 0))
        : undefined;
    if (!def && r && stack.name) {
        const items = r.getAllItems?.() ?? [];
        def = items.find((i) => i.name === stack.name && i.metadata === stack.damage)
            ?? items.find((i) => i.name === stack.name);
    }
    const name = def?.name ?? stack.name;
    const isPotion = POTION_IDS.has(stack.itemId) || name === "potion" || name === "splash_potion" || name === "lingering_potion";
    if (isPotion) return potionDisplayName(stack.damage, r, n.customPotionEffects);
    if (def && r) {
        const localized = r.getLocalizedName(def.key);
        if (localized) return localized;
    }
    if (def) return def.displayName;
    const custom = r?.getCustomName(stack.itemId, stack.damage);
    if (custom) return custom;
    return `Item ${stack.itemId}:${stack.damage}`;
}

export function potionDisplayName(
    damage: number,
    resolver: CraftingResolverService | null | undefined,
    effects?: ItemStackNbt["customPotionEffects"],
): string {
    const name = (mcKey: string) => resolver?.getMinecraftName(mcKey);
    const decoded = decodePotionDamage(damage);
    const prefix = decoded.splash ? (name("potion.splash_prefix") ?? "Splash ") : "";

    // ItemPotion.getEffects reads CustomPotionEffects ahead of the damage value, and a potion handed
    // out by command carries its effects there while leaving the damage value at water.
    const customKey = customPotionEffectKey(effects);
    if (customKey) {
        return prefix + (name(`minecraft:potion:effect:${customKey}`) ?? defaultEffectName(customKey));
    }

    if (decoded.empty) return name("minecraft:potion:0") ?? "Water Bottle";
    const effectKey = potionEffectKey(decoded);
    const base = name(`minecraft:potion:effect:${effectKey}`) ?? defaultEffectName(effectKey);
    const modifiers: string[] = [];
    if (decoded.tierII) modifiers.push(name("potion.modifier.ii") ?? "II");
    if (decoded.extended) modifiers.push(name("potion.modifier.extended") ?? "Extended");
    const suffix = modifiers.length > 0 ? ` ${modifiers.join(" ")}` : "";
    return `${prefix}${base}${suffix}`;
}

function defaultEffectName(key: string): string {
    const map: Record<string, string> = {
        awkward: "Awkward Potion",
        regeneration: "Potion of Regeneration",
        swiftness: "Potion of Swiftness",
        fire_resistance: "Potion of Fire Resistance",
        poison: "Potion of Poison",
        healing: "Potion of Healing",
        night_vision: "Potion of Night Vision",
        clear: "Clear Potion",
        weakness: "Potion of Weakness",
        strength: "Potion of Strength",
        slowness: "Potion of Slowness",
        leaping: "Potion of Leaping",
        harming: "Potion of Harming",
        water_breathing: "Potion of Water Breathing",
        invisibility: "Potion of Invisibility",
        haste: "Potion of Haste",
        mining_fatigue: "Potion of Mining Fatigue",
        nausea: "Potion of Nausea",
        resistance: "Potion of Resistance",
        blindness: "Potion of Blindness",
        hunger: "Potion of Hunger",
        wither: "Potion of Wither",
        health_boost: "Potion of Health Boost",
        absorption: "Potion of Absorption",
        saturation: "Potion of Saturation",
        thick: "Thick Potion",
        unknown: "Potion",
    };
    return map[key] ?? "Potion";
}
